import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useOptionalI18n } from '@/shared/i18n/useT';
import { useOrg } from '@/shared/org';
import { EMPTY_FORMULA_CONFIG, type GridFormulaConfig } from '@/shared/formula/engine';
import { normaliseGridFormulas } from '@/shared/formula/storage';
import { supabase } from '@/lib/supabase';
import { applyThemeColors, type ThemeColors } from '@/shared/theme/themeColors';
import {
  fetchDocumentTheme,
  themeFromSettings,
} from '@/shared/theme/ThemeColorsProvider';
import { normalizeEnabledCurrencies, type Currency } from '@/shared/currencies';
import { normalizeAlignment } from './alignment';
import {
  migrateStoredFormats,
  decimalSeparatorFor,
  getGridDecimalSeparator,
  normalizeGridDataFormats,
  setGridDecimalSeparator,
  type GridDataFormats,
  type GridFormats,
} from './formats';
import {
  GRID_SETTING_KEYS,
  loadGridSettings,
  saveGridSetting,
  type GridSettingKey,
} from './settings';
import type {
  GridColumnDevices,
  GridColumnOrder,
  GridGrouping,
  GridFrozenColumns,
  GridHeaders,
  GridHiddenColumns,
  GridItemNames,
  GridRowOrder,
  GridSort,
  GridWidths,
} from './headers';
import type { GridAlignment, TabsPlacement } from './types';
import type { Json } from '@/lib/database.types';
import type { DeviceScope } from '@/shared/device/device';
import type { GridChartSettings } from './GridChartPanel';

/**
 * Everything a grid reads that is shared by the whole organization.
 *
 * This is the seam that replaces TMS's `ConfigContext`. There it was one global
 * settings blob for the single company the app served; here every value is
 * scoped to `org_id`, loaded once when the organization is known, and written
 * back only by users with format authority — the same shared-setting boundary
 * enforced by the database's `sheet_module_level`/document access helpers.
 *
 * The decimal separator is the one field that did NOT come across as a setting.
 * It follows the active locale through `Intl` (see `formats.ts`), because a
 * stored separator and a translated interface drift apart the moment someone
 * switches language.
 */
import { normalizeGridSummaryCards, type GridSummaryCardsConfig } from './summaryCards';
import type { ColumnGroup } from './columnGroups';

/** Fields whose aggregate is hidden in group subtotals and in the totals row. */
export interface GridHiddenAggregates {
  readonly subtotals?: readonly string[];
  readonly totals?: readonly string[];
  /** The label in the totals row's first cell; unset shows "Grand Total". */
  readonly totalsLabel?: string;
}

/** How one grid shows its aggregates. Unset fields fall back to defaults. */
export interface GridAggregateDisplay {
  readonly cardsVisible?: boolean;
  /** The devices the cards show on. Missing means every device. */
  readonly cardsOn?: DeviceScope;
  readonly cardsPlacement?: 'top' | 'topBelow' | 'bottom' | 'sidebar';
  /** The devices the page's special controls (currency pair, refresh…) show on. Missing means every device. */
  readonly controlsOn?: DeviceScope;
  readonly controlsPlacement?: 'top' | 'topBelow' | 'bottom' | 'sidebar';
  /** The "+Σ" button that adds a card. Missing means shown. */
  readonly addCardButton?: boolean;
  readonly totalsRow?: boolean;
  readonly groupSubtotals?: boolean;
  /** Where group subtotals sit: in each group's header band (`top`, the default) or below its rows. */
  readonly groupSubtotalsPosition?: 'top' | 'bottom';
  /** An empty, read-only row closing each group. */
  readonly groupBlankRows?: boolean;
  /** A parent group whose rows all share one second-level value shows as one group, "Parent Child". */
  readonly groupMergeSingle?: boolean;
  /** Outline: no header band over an open group; its value shows once, on its first row, with a −/+ button. */
  readonly groupOutline?: boolean;
}

export interface GridConfig {
  /** The owner organization of these settings. */
  readonly orgId: string | null;
  /**
   * The Folders document these settings belong to, or `null` for the
   * organization-wide set the module grids use.
   *
   * The two are different rows and different permissions: an organization-wide
   * preference reorders columns for every colleague and stays with admins, a
   * document's preference is the document's and follows edit access to it.
   */
  readonly documentId: string | null;
  /**
   * May define formulas and hand-override computed cells. A client-side
   * convenience only: `private.has_capability(org_id, 'formulas')` in RLS is
   * what actually decides, and it is asked as a capability rather than a rank
   * because editing what computes over every tenant's data is a different job
   * from managing staff (CLAUDE.md).
   */
  readonly canEditFormulas: boolean;
  readonly canEditData: boolean;
  readonly canEditFormats: boolean;
  readonly formats: GridFormats;
  readonly dataFormats: GridDataFormats;
  readonly decimalSeparator: '.' | ',';
  readonly currencies: readonly Currency[];
  readonly headers: GridHeaders;
  readonly columnOrder: GridColumnOrder;
  readonly rowOrder: GridRowOrder;
  readonly sort: GridSort;
  readonly grouping: GridGrouping;
  readonly widths: GridWidths;
  readonly hiddenColumns: GridHiddenColumns;
  readonly frozenColumns: GridFrozenColumns;
  /** `columnsKey` → columns shown on one device only. */
  readonly columnDevices: GridColumnDevices;
  readonly itemNames: GridItemNames;
  /** `gridKey` → whether its group headers show a count. Missing means yes. */
  readonly groupCounts: Record<string, boolean>;
  /** `gridKey` → fields whose last-change-time column is shown to their right. */
  readonly changeTimeColumns: Record<string, string[]>;
  /** `gridKey` → field → the note on that column's header. */
  readonly headerNotes: Record<string, Record<string, string>>;
  /** `columnsKey` → its column outline groups. */
  readonly columnGroups: Record<string, ColumnGroup[]>;
  /** `gridKey` → fields whose subtotals / totals-row value are hidden. */
  readonly hiddenAggregates: Record<string, GridHiddenAggregates>;
  /** `gridKey` → the chart panel settings. */
  readonly chart: Record<string, GridChartSettings>;
  /** Per gridKey: cards, totals row and group subtotals display. */
  readonly aggregateDisplay: Record<string, GridAggregateDisplay>;
  readonly tabsPlacement: TabsPlacement;
  /** Tab placement on mobile, or null to follow `tabsPlacement`. */
  readonly tabsPlacementMobile: TabsPlacement | null;
  readonly alignment: GridAlignment;
  readonly formulas: Record<string, GridFormulaConfig>;
  /** Custom configured summary cards per gridKey */
  readonly summaryCards: GridSummaryCardsConfig;
  /** Whether the current user may write these shared settings at all. */
  readonly canEditShared: boolean;
  /**
   * A read-only document whose reader may still hide columns and pick a chart
   * for themselves (the Numtable library). Absent everywhere else.
   */
  readonly personalView?: boolean;
  readonly loading: boolean;
  /** Persist one key and keep the local copy in step. */
  readonly save: (key: GridSettingKey, value: unknown) => Promise<Error | null>;
  /** One key as stored, latest saves included: what an undo puts back. */
  readonly read?: (key: GridSettingKey) => unknown;
  readonly reload: () => void;
  /**
   * Grids whose columns are linked (`gridKey` → the other gridKeys in its link). A
   * column-layout change saved for one is saved for all of them too; see
   * `LINKED_COLUMN_KEYS`.
   */
  readonly setLinkedGridKeys?: (links: Readonly<Record<string, readonly string[]>>) => void;
  /** Copies every column-layout setting of `from` onto `to`, for a grid that has just been linked. */
  readonly copyColumnLayout?: (from: string, to: string) => Promise<Error | null>;
}

/** The per-grid settings that describe columns, kept identical across linked grids. */
const LINKED_COLUMN_KEYS: readonly GridSettingKey[] = [
  GRID_SETTING_KEYS.headers,
  GRID_SETTING_KEYS.columnOrder,
  GRID_SETTING_KEYS.grouping,
  GRID_SETTING_KEYS.widths,
  GRID_SETTING_KEYS.hiddenColumns,
  GRID_SETTING_KEYS.frozenColumns,
  GRID_SETTING_KEYS.columnDevices,
  GRID_SETTING_KEYS.dataFormats,
  GRID_SETTING_KEYS.columnColors,
  GRID_SETTING_KEYS.headerColors,
  GRID_SETTING_KEYS.headerNotes,
  GRID_SETTING_KEYS.columnGroups,
];

const sameJson = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/** `value` with each linked grid's entry following the one that changed from `previous`. */
function mirrorLinked(value: unknown, previous: unknown, links: Readonly<Record<string, readonly string[]>>): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const next = value as Record<string, unknown>;
  const before = previous && typeof previous === 'object' && !Array.isArray(previous) ? previous as Record<string, unknown> : {};
  let out: Record<string, unknown> | null = null;
  for (const [gridKey, siblings] of Object.entries(links)) {
    if (sameJson(next[gridKey], before[gridKey])) continue;
    out ??= { ...next };
    for (const sibling of siblings) {
      if (next[gridKey] === undefined) delete out[sibling];
      else out[sibling] = next[gridKey];
    }
  }
  return out ?? value;
}

const EMPTY: Omit<
  GridConfig,
  | 'canEditShared'
  | 'canEditData'
  | 'canEditFormats'
  | 'loading'
  | 'save'
  | 'read'
  | 'reload'
  | 'formats'
  | 'dataFormats'
  | 'decimalSeparator'
  | 'currencies'
  | 'alignment'
  | 'orgId'
  | 'documentId'
  | 'canEditFormulas'
> = {
  headers: {},
  columnOrder: {},
  rowOrder: {},
  sort: {},
  grouping: {},
  widths: {},
  hiddenColumns: {},
  frozenColumns: {},
  columnDevices: {},
  itemNames: {},
  groupCounts: {},
  changeTimeColumns: {},
  headerNotes: {},
  columnGroups: {},
  hiddenAggregates: {},
  aggregateDisplay: {},
  chart: {},
  tabsPlacement: 'top',
  tabsPlacementMobile: null,
  formulas: {},
  summaryCards: {},
};

export const GridConfigContext = createContext<GridConfig | null>(null);

/** Narrow a loaded jsonb blob to a record, without trusting its shape. */
function asRecord<T>(value: unknown): T {
  return (value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {}) as T;
}

function accessRank(level: unknown): number {
  if (level === 'edit_formula') return 4;
  if (level === 'edit_format') return 3;
  if (level === 'edit_data' || level === 'edit') return 2;
  if (level === 'view') return 1;
  return 0;
}

function rpcAccessLevel(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const first = value[0];
  if (!first || typeof first !== 'object' || Array.isArray(first)) return null;
  const level = (first as { level?: unknown }).level;
  return typeof level === 'string' ? level : null;
}

interface ProviderProps {
  children: ReactNode;
  /** Optional document access resolved by the Sheets/Folders layer. */
  capabilities?: Partial<Pick<GridConfig, 'canEditData' | 'canEditFormats' | 'canEditFormulas'>>;
  /**
   * Scope the settings to one Folders document. Omitted at the root of the app,
   * where the provider serves the module grids; supplied by the Sheet editor,
   * which nests a second provider for the document it has open.
   */
  documentId?: string | null;
}

export function GridConfigProvider({ children, documentId = null, capabilities }: ProviderProps) {
  const org = useOrg();
  const i18n = useOptionalI18n();
  const locale = i18n?.locale ?? 'en';

  const [stored, setStored] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const [organizationFormatPermission, setOrganizationFormatPermission] = useState(false);
  const [organizationFormatPermissionReady, setOrganizationFormatPermissionReady] = useState(false);
  const [documentCapabilities, setDocumentCapabilities] = useState<Pick<GridConfig, 'canEditData' | 'canEditFormats' | 'canEditFormulas'> | null>(null);
  const [documentOrgId, setDocumentOrgId] = useState<string | null>(null);

  const activeOrgId = org.status === 'ready' ? org.orgId : null;
  // A shared document can belong to another organization than the one selected
  // in the recipient's shell. Grid settings belong to the document owner, not
  // to that personal/active organization.
  const orgId = documentId === null ? activeOrgId : documentOrgId;
  const membershipId = org.status === 'ready' ? org.membership.id : null;
  const role = org.status === 'ready' ? org.role : null;

  useEffect(() => {
    if (documentId === null) {
      setDocumentOrgId(null);
      return;
    }

    let active = true;
    setDocumentOrgId(null);
    void supabase
      .from('folders_nodes')
      .select('org_id')
      .eq('id', documentId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (active) setDocumentOrgId(error ? null : data?.org_id ?? null);
      });

    return () => {
      active = false;
    };
  }, [documentId]);

  useEffect(() => {
    if (documentId !== null || !orgId || !membershipId) {
      setOrganizationFormatPermission(false);
      setOrganizationFormatPermissionReady(documentId !== null || !orgId);
      return;
    }

    if (role === 'owner' || role === 'developer') {
      setOrganizationFormatPermission(true);
      setOrganizationFormatPermissionReady(true);
      return;
    }

    let active = true;
    setOrganizationFormatPermission(false);
    setOrganizationFormatPermissionReady(false);

    void supabase
      .from('module_permissions')
      .select('level')
      .eq('membership_id', membershipId)
      .eq('module_key', 'sheets')
      .maybeSingle()
      .then(({ data, error }) => {
        if (!active) return;
        setOrganizationFormatPermission(!error && accessRank(data?.level) >= 3);
        setOrganizationFormatPermissionReady(true);
      });

    return () => {
      active = false;
    };
  }, [activeOrgId, documentId, membershipId, orgId, role]);

  useEffect(() => {
    if (documentId === null || capabilities || !orgId) {
      setDocumentCapabilities(null);
      return;
    }

    let active = true;
    setDocumentCapabilities(null);

    void supabase.rpc('sheet_effective_access', { p_document: documentId }).then(({ data, error }) => {
      if (!active) return;
      const level = error ? null : rpcAccessLevel(data);
      const rank = accessRank(level);
      setDocumentCapabilities({
        canEditData: rank >= 2,
        canEditFormats: rank >= 3,
        canEditFormulas: rank >= 4,
      });
    });

    return () => {
      active = false;
    };
  }, [capabilities, documentId, orgId]);
  // Organization-wide settings change what every colleague sees, so they require
  // format authority. Document settings follow the effective access of that file.
  // RLS remains the final enforcement boundary.
  const resolvedCapabilities = capabilities ?? documentCapabilities;
  const canActInGrid = org.status === 'ready' && !org.isViewingAs;
  const canEditShared =
    canActInGrid &&
    (documentId === null
      ? organizationFormatPermissionReady && organizationFormatPermission
      : resolvedCapabilities?.canEditFormats === true);
  // View mode actively restricts interface capabilities (TASK-4). This only
  // applies while previewing as a lower role: `org.activeViewMode` is that
  // override and stays null otherwise, including for a real owner/admin/etc.
  // acting under their own role — falling back to `org.role` here used to
  // gate everyone whose actual role wasn't 'developer'/'designer', which
  // silently stripped owners of format and formula editing.
  const activeViewMode = org.status === 'ready' ? org.activeViewMode : null;
  const viewModeAllowsFormulas = activeViewMode === null || activeViewMode === 'developer';
  const viewModeAllowsFormats = activeViewMode === null || activeViewMode === 'developer' || activeViewMode === 'designer';
  const viewModeAllowsData = activeViewMode === null || activeViewMode !== 'viewer';

  const baseEditData = canActInGrid && (resolvedCapabilities?.canEditData ?? (
    documentId === null ? org.role !== 'viewer' : false
  ));
  const baseEditFormats = canActInGrid && (resolvedCapabilities?.canEditFormats ?? canEditShared);
  const baseEditFormulas = canActInGrid && (resolvedCapabilities?.canEditFormulas ?? (
    documentId === null && (org.role === 'owner' || org.role === 'developer')
  ));

  const canEditData = baseEditData && viewModeAllowsData;
  const canEditFormats = baseEditFormats && viewModeAllowsFormats;
  const canEditFormulas = baseEditFormulas && viewModeAllowsFormulas;

  // Drop a previous scope's settings before the next request resolves. This
  // includes navigating between shared documents. The organization-wide palette
  // is NOT cleared here: `ThemeColorsProvider` is its sole writer, and a second
  // injector for the same custom properties is exactly the race the old
  // `applyGridColors` had.
  useLayoutEffect(() => {
    setStored({});
  }, [activeOrgId, documentId]);

  useEffect(() => {
    if (!orgId) {
      setStored({});
      setLoading(false);
      return;
    }

    let active = true;
    let restoreColors: (() => void) | null = null;
    setLoading(true);

    void (async () => {
      try {
        const settings = await loadGridSettings(orgId, documentId);
        if (!active) return;
        setStored(settings as Record<string, unknown>);

        // Colours are applied as a side effect rather than read by the grid:
        // they are CSS variables, so nothing has to re-render for them to take.
        // Only the document path does this — outside a document the palette
        // belongs to `ThemeColorsProvider` at the app root.
        if (documentId !== null) {
          // A document must render in ITS OWNING organization's colours, and
          // `orgId` here is already that org rather than the viewer's active
          // one. The direct read is still not enough: `grid_settings_select`
          // gates organization-wide rows on `private.is_member(org_id)`, so a
          // colleague of another tenant holding a share silently gets no
          // org-wide row back and the document would be painted in the
          // viewer's own palette. `document_theme` is the security-definer
          // escape hatch for exactly that, and it merges the org theme with the
          // document override server-side.
          //
          // It is asked first, always: `document_org_grid_settings` omits
          // `theme_colors`, so `settings` only ever carries the legacy
          // `grid_colors` row here, and reading that first painted every sheet
          // in its upgrade (Indigo) whatever preset the tenant had picked. The
          // legacy row is only the fallback for a tenant with no theme at all.
          const crossTenant = activeOrgId !== null && orgId !== activeOrgId;
          const resolved = await fetchDocumentTheme(documentId);
          if (!active) return;
          const nextColors: ThemeColors | null = resolved ?? themeFromSettings(settings);
          // The undo closure is what puts the parent palette back when the
          // sheet unmounts. Cross-tenant we inject even when the owning org
          // chose nothing, so the viewer's own colours do not leak in as a
          // stand-in for a theme that does not exist.
          if (nextColors || crossTenant) {
            restoreColors = applyThemeColors(nextColors);
          }
        }
      } catch {
        if (active) setStored({});
      }
      if (active) setLoading(false);
    })();

    return () => {
      active = false;
      restoreColors?.();
    };
  }, [activeOrgId, orgId, documentId, nonce]);

  const linksRef = useRef<Readonly<Record<string, readonly string[]>>>({});
  const setLinkedGridKeys = useCallback((links: Readonly<Record<string, readonly string[]>>) => {
    linksRef.current = links;
  }, []);

  const storedRef = useRef(stored);
  storedRef.current = stored;
  const read = useCallback((key: GridSettingKey) => storedRef.current[key], []);

  const save = useCallback(
    async (key: GridSettingKey, requested: unknown): Promise<Error | null> => {
      const value = LINKED_COLUMN_KEYS.includes(key) ? mirrorLinked(requested, stored[key], linksRef.current) : requested;
      if (!orgId) return new Error(documentId ? 'Document configuration is still loading' : 'No active organization');
      // Optimistic: the grid has already drawn the change, and a shared setting
      // that snapped back on every save would be unusable to drag against.
      const previous = stored[key];
      setStored((current) => ({ ...current, [key]: value }));
      const error = await saveGridSetting(orgId, key, value as Json, documentId);
      if (error) {
        setStored((current) => {
          if (current[key] !== value) return current;
          const next = { ...current };
          if (previous === undefined) delete next[key];
          else next[key] = previous;
          return next;
        });
      }
      return error;
    },
    [documentId, orgId, stored],
  );

  const copyColumnLayout = useCallback(async (from: string, to: string): Promise<Error | null> => {
    for (const key of LINKED_COLUMN_KEYS) {
      const current = stored[key];
      const map = current && typeof current === 'object' && !Array.isArray(current) ? current as Record<string, unknown> : {};
      if (sameJson(map[from], map[to])) continue;
      const next = { ...map };
      if (map[from] === undefined) delete next[to];
      else next[to] = map[from];
      const error = await save(key, next);
      if (error) return error;
    }
    return null;
  }, [save, stored]);

  const value = useMemo<GridConfig>(
    () => ({
      ...EMPTY,
      orgId,
      documentId,
      canEditFormulas,
      canEditData,
      canEditFormats,
      formats: migrateStoredFormats(stored[GRID_SETTING_KEYS.globalFormats]),
      dataFormats: normalizeGridDataFormats(stored[GRID_SETTING_KEYS.dataFormats]),
      decimalSeparator:
        stored[GRID_SETTING_KEYS.decimalSeparator] === ',' ||
        stored[GRID_SETTING_KEYS.decimalSeparator] === '.'
          ? (stored[GRID_SETTING_KEYS.decimalSeparator] as '.' | ',')
          : decimalSeparatorFor(locale),
      currencies: normalizeEnabledCurrencies(stored[GRID_SETTING_KEYS.currencies]),
      headers: asRecord<GridHeaders>(stored[GRID_SETTING_KEYS.headers]),
      columnOrder: asRecord<GridColumnOrder>(stored[GRID_SETTING_KEYS.columnOrder]),
      rowOrder: asRecord<GridRowOrder>(stored[GRID_SETTING_KEYS.rowOrder]),
      sort: asRecord<GridSort>(stored[GRID_SETTING_KEYS.sort]),
      grouping: asRecord<GridGrouping>(stored[GRID_SETTING_KEYS.grouping]),
      widths: asRecord<GridWidths>(stored[GRID_SETTING_KEYS.widths]),
      hiddenColumns: asRecord<GridHiddenColumns>(stored[GRID_SETTING_KEYS.hiddenColumns]),
      frozenColumns: asRecord<GridFrozenColumns>(stored[GRID_SETTING_KEYS.frozenColumns]),
      columnDevices: asRecord<GridColumnDevices>(stored[GRID_SETTING_KEYS.columnDevices]),
      itemNames: asRecord<GridItemNames>(stored[GRID_SETTING_KEYS.itemNames]),
      groupCounts: asRecord<Record<string, boolean>>(stored[GRID_SETTING_KEYS.groupCounts]),
      changeTimeColumns: asRecord<Record<string, string[]>>(stored[GRID_SETTING_KEYS.changeTimeColumns]),
      headerNotes: asRecord<Record<string, Record<string, string>>>(stored[GRID_SETTING_KEYS.headerNotes]),
      columnGroups: asRecord<Record<string, ColumnGroup[]>>(stored[GRID_SETTING_KEYS.columnGroups]),
      hiddenAggregates: asRecord<Record<string, GridHiddenAggregates>>(stored[GRID_SETTING_KEYS.hiddenAggregates]),
      aggregateDisplay: asRecord<Record<string, GridAggregateDisplay>>(stored[GRID_SETTING_KEYS.aggregateDisplay]),
      chart: asRecord<Record<string, GridChartSettings>>(stored[GRID_SETTING_KEYS.chart]),
      tabsPlacement: (() => {
        const raw = stored[GRID_SETTING_KEYS.tabsPlacement];
        if (raw === 'top' || raw === 'bottom' || raw === 'left') return raw;
        return 'top';
      })(),
      tabsPlacementMobile: (() => {
        const raw = stored[GRID_SETTING_KEYS.tabsPlacementMobile];
        return raw === 'top' || raw === 'bottom' || raw === 'left' ? raw : null;
      })(),
      alignment: normalizeAlignment(stored[GRID_SETTING_KEYS.alignment]),
      formulas: normaliseGridFormulas(stored[GRID_SETTING_KEYS.formulas]),
      summaryCards: normalizeGridSummaryCards(stored[GRID_SETTING_KEYS.summaryCards]),
      canEditShared,
      loading,
      save,
      read,
      reload: () => setNonce((n) => n + 1),
      setLinkedGridKeys,
      copyColumnLayout,
    }),
    [stored, locale, canEditShared, canEditData, canEditFormats, canEditFormulas, loading, save, read, orgId, documentId, org, setLinkedGridKeys, copyColumnLayout],
  );

  useLayoutEffect(() => {
    const separator = stored[GRID_SETTING_KEYS.decimalSeparator];
    const next = separator === ',' || separator === '.' ? separator : decimalSeparatorFor(locale);
    const previous = getGridDecimalSeparator();
    setGridDecimalSeparator(next);
    return () => setGridDecimalSeparator(previous);
  }, [stored, locale]);

  // The numpad's decimal key always types the decimal separator chosen in settings, as in
  // Excel: with a comma separator it writes a comma, whatever the keyboard layout sends.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'NumpadDecimal' || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      const separator = getGridDecimalSeparator();
      if (event.key === separator) return;
      const target = event.target;
      const typesText = target instanceof HTMLTextAreaElement
        || (target instanceof HTMLInputElement && ['text', 'search', 'tel', ''].includes(target.type))
        || (target instanceof HTMLElement && target.isContentEditable);
      if (!typesText) return;
      event.preventDefault();
      // insertText keeps the field's undo history and fires the input event React listens to.
      document.execCommand('insertText', false, separator);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  return (
    <GridConfigContext.Provider value={value}>{children}</GridConfigContext.Provider>
  );
}

/**
 * A grid outside the provider is a bug worth failing loudly for: silently
 * falling back to defaults would mean one tenant's grid quietly ignoring its
 * own saved layout.
 */
export function useGridConfig(): GridConfig {
  const config = useContext(GridConfigContext);
  if (!config) {
    throw new Error('useGridConfig must be used inside <GridConfigProvider>');
  }
  return config;
}

/** The formulas of one grid, or an empty set. */
export function useGridFormulas(gridKey: string): GridFormulaConfig {
  const { formulas } = useGridConfig();
  return formulas[gridKey] ?? EMPTY_FORMULA_CONFIG;
}
